import * as React from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import {
	Layout,
	Button,
	InputNumber,
	Input,
	Alert,
	Popover,
	Modal,
	Row,
	Checkbox,
	Typography,
} from 'antd';
import {
	CaretDownOutlined,
	CaretUpOutlined,
	InfoCircleOutlined,
	MinusCircleOutlined,
} from '@ant-design/icons';
import {
	getCurrency,
	nullSafeGet,
	nullSafeGetOrElse,
	nullSafeGetWithZero,
} from '../../utils/DataAccessUtils';
import { useEffect, useMemo, useState } from 'react';
import { defaultTier } from '../../utils/ApprovalHierarchyUtils';

const queryString = require('qs');
const { Content } = Layout;

const style = require('./ApprovalTiersComponent.less');

const getMaxAmount = (tiers) => {
	const tierList = tiers.slice(0, -1);
	const amounts = tierList.map((t, i) => {
		return t.amountUpto;
	});
	return Math.max(...amounts, 0);
};

function ApprovalTiersComponent({
	approvalHierarchy,
	visible,
	title,
	onBack,
	onNext,
	setApprovalHierarchyTiers,

	isNTEBased,
	setIsNTEBased,
	currentUser,
}) {
	const setupTiers = (tiers) =>
		tiers.map((tier, i, tiers) => {
			const isNotLast = tiers.length - 1 !== i;

			const amountUpto = isNotLast
				? isNTEBased && isNaN(tier.amountUpto)
					? 0
					: tier.amountUpto
				: undefined;
			return {
				id: i,
				...tier,
				amountUpto,
			};
		});
	const tiers = useMemo(
		() =>
			nullSafeGet('tierDetails.0', approvalHierarchy)
				? setupTiers(nullSafeGet('tierDetails', approvalHierarchy))
				: setupTiers([defaultTier]),
		[approvalHierarchy]
	);

	const updateTiers = (tiers) => {
		const newTiers = tiers.map((tier, i) => {
			return {
				...tier,
				label: i + 1,
				...(tier.amountUpto !== undefined ? { amountUpto: tier.amountUpto.toString() } : {}),
			};
		});
		setApprovalHierarchyTiers(newTiers);
	};

	const handleEscalationStep = (t, step) => {
		const tierList = tiers.slice();
		tierList.splice(t.label - 1, 1, {
			...t,
			daysAfterEscalation: step,
			showSearch: false,
		});
		updateTiers(tierList);
	};
	const handleEscalationCheck = (t, e) => {
		const tierList = tiers.slice();
		tierList.splice(t.label - 1, 1, {
			...t,
			showSearch: false,
			escalationVisible: e.target.checked,
			daysAfterEscalation: e.target.checked ? 0 : undefined,
		});
		updateTiers(tierList);
	};
	const handleEscalationInputBlur = (tier, e) => {
		const value = parseFloat(e.target.value.replace(new RegExp(/\$\s?|(,*)/g), ''));
		const tierList = tiers.slice();
		tierList.splice(tier.label - 1, 1, { ...tier, daysAfterEscalation: value });
		updateTiers(tierList);
	};
	const handleNameInputBlur = (tier, e) => {
		tiers.splice(tier.label - 1, 1, {
			...tier,
			name: e.target.value || tier.name,
		});
		updateTiers(tiers);
	};
	const moveTierUp = (i, e) => {
		if (i > 0) {
			const tierList = tiers.slice();
			const swapTier = tierList[i - 1];
			tierList[i - 1] = tierList[i];
			tierList[i] = swapTier;
			if (tierList[i - 1].amountUpto === undefined) {
				tierList[i - 1].amountUpto = swapTier.amountUpto;
				tierList[i].amountUpto = undefined;
			}
			const newTiers = tierList.map((tier, i) => {
				return { ...tier, label: i + 1 };
			});
			updateTiers(newTiers);
		}
	};
	const moveTierDown = (i, e) => {
		const tierList = tiers.slice();
		const swapTier = tierList[i];
		tierList[i] = tierList[i + 1];
		tierList[i + 1] = swapTier;
		if (tierList[i].amountUpto === undefined) {
			tierList[i].amountUpto = swapTier.amountUpto;
			tierList[i + 1].amountUpto = undefined;
		}
		const newTiers = tierList.map((tier, i) => {
			return { ...tier, label: i + 1 };
		});
		updateTiers(newTiers);
	};
	const handleInputBlur = (tier, e) => {
		const value = parseFloat(e.target.value.replace(new RegExp(/\$\s?|(,*)/g), ''));
		const tierList = tiers.slice();
		tierList.splice(tier.label - 1, 1, { ...tier, amountUpto: isNaN(value) ? 0 : value });
		updateTiers(tierList);
	};
	const removeApprovalTier = (i, e) => {
		const tierList = tiers.slice();
		tierList.splice(i, 1);
		if (tierList && tierList.length > 1) {
			delete tierList[tierList.length - 1].amountUpto;
		}
		updateTiers(tierList);
	};
	const addApprovalTier = (e) => {
		const tierList = tiers.slice();
		let ids, nextAmount, nextid;
		if (tierList.length > 0) {
			ids = tierList.map((tier) => {
				return tier.id;
			});
			nextAmount = getMaxAmount(tierList) * 5;
			nextid = Math.max(...ids) + 1;
			tierList[tierList.length - 1]['amountUpto'] = nextAmount;
		}

		//SET TOP TIER TO NO AMOUNT UP TO
		tierList.push({
			id: nextid,
			name: '',
			approvers: [],
			showSearch: false,
		});
		updateTiers(tierList);
	};

	const handleStep = (t, step) => {
		const tierList = tiers.slice();
		tierList.splice(t.label - 1, 1, {
			...t,
			amountUpto: step,
		});
		updateTiers(tierList);
	};

	const previousAmounts = [];
	const warnings = [];
	const currency = getCurrency({ currentUser });

	return (
		<Modal
			visible={visible}
			width={840}
			title={title}
			destroyOnClose={true}
			bodyStyle={{ overflow: 'hidden', padding: '12px 24px' }}
			footer={
				<Row>
					<div style={{ marginLeft: 'auto' }}>
						<Button onClick={onBack}>Back</Button>
						<Button type={'primary'} style={{ marginLeft: '8px' }} onClick={onNext}>
							Next
						</Button>
					</div>
				</Row>
			}
			closable={false}
			maskClosable={false}
		>
			<div className="approvalTiersComponent">
				<div className="ApprovalTierHeader">
					<div className="ApprovalUpDownWrapperHeader"></div>
					<div className="ApprovalTierCenter">
						<div className="ApprovalTierIDHeader">Order</div>
						<div className="ApprovalTierLabelHeader">Name</div>
					</div>
					<div className="ApprovalTierInputWrapper">
						{isNTEBased ? <div className="ApprovalTierInputHeader">Amounts</div> : null}
						<div className="ApprovalTierEscalationHeader">
							<span style={{ marginRight: 3, marginLeft: 29 }}>Amount Based</span>
							<Popover content={'Typically would be disabled for work order approval hierarchies.'}>
								<InfoCircleOutlined style={{ marginTop: 3 }} />
							</Popover>
							<Checkbox
								style={{ marginLeft: 4 }}
								checked={isNTEBased}
								onChange={(element) => {
									setIsNTEBased(nullSafeGetOrElse('target.checked', element, false));
								}}
							/>
						</div>
					</div>
				</div>
				<div className="ApprovalTierBody">
					{tiers.map((tier, i, tiers) => {
						const isNotLast = tiers.length - 1 !== i;
						const tierAmount =
							tier.amountUpto !== undefined ? parseFloat(tier.amountUpto) : undefined;
						const warning =
							isNotLast && isNTEBased
								? previousAmounts.some((amount) => parseFloat(amount) > tierAmount)
								: false;
						if (warning) {
							warnings.push(tier);
						}
						const hasEscalation = tier.escalationVisible || tier.daysAfterEscalation !== undefined;
						previousAmounts.push(tier.amountUpto);
						return (
							<div
								className="ApprovalTier"
								key={tier.label && tier.label.toString()}
								style={{
									backgroundColor: warning ? 'rgba(255,77,79,0.12)' : null,
								}}
							>
								<div style={{ lineHeight: '16px' }} className={'ApprovalUpDownWrapper'}>
									<Popover
										content={'Click to move this tier up.'}
										placement={'left'}
										style={{ lineHeight: '16px' }}
										disabled={i === 0}
									>
										<Button
											onClick={moveTierUp.bind(this, i)}
											icon={<CaretUpOutlined style={{ fontSize: '12px' }} />}
											type={'text'}
											disabled={i === 0}
										></Button>
									</Popover>
									<Popover
										content={'Click to move this tier down.'}
										placement={'left'}
										style={{ lineHeight: '16px' }}
										disabled={!isNotLast}
									>
										<Button
											onClick={moveTierDown.bind(this, i)}
											icon={<CaretDownOutlined style={{ fontSize: '12px' }} />}
											type={'text'}
											disabled={!isNotLast}
										></Button>
									</Popover>
								</div>
								<div className="ApprovalTierCenter">
									<div className="ApprovalTierID">{i + 1}</div>
									<div className="ApprovalTierLabel">
										<Input
											key={tier.name}
											defaultValue={tier.name}
											placeholder={'Tier '.concat(tier.label)}
											onBlur={handleNameInputBlur.bind(this, tier)}
										/>
									</div>
								</div>
								{isNotLast ? (
									<div className="ApprovalTierInputWrapper">
										<div className="ApprovalTierPrefix">
											{isNTEBased ? (
												<>
													<span>Up to</span>
													<InputNumber
														style={{ width: 160 }}
														step={100}
														value={tier.amountUpto}
														onStep={handleStep.bind(this, tier)}
														onBlur={handleInputBlur.bind(this, tier)}
														min={0}
														addonAfter={currency.id}
														formatter={(value) =>
															`$ ${value}`.replace(new RegExp(/\B(?=(\d{3})+(?!\d))/g), ',')
														}
														parser={(value) => value.replace(new RegExp(/\$\s?|(,*)/g), '')}
													/>
												</>
											) : null}
										</div>
										<div className="ApprovalTierPrefix" style={{ marginLeft: 44 }}>
											{hasEscalation ? null : <span>Escalate?</span>}
											<Checkbox
												checked={hasEscalation}
												onChange={handleEscalationCheck.bind(this, tier)}
											/>
											{hasEscalation ? (
												<InputNumber
													style={{ width: 140 }}
													step={1}
													value={tier.daysAfterEscalation}
													onStep={handleEscalationStep.bind(this, tier)}
													onBlur={handleEscalationInputBlur.bind(this, tier)}
													min={0}
													addonAfter={'days'}
												/>
											) : null}
										</div>
									</div>
								) : isNTEBased ? (
									<div className="ApprovalTierInputWrapper">
										<div className="ApprovalTierPrefix">
											<span>Greater than {currency.format(getMaxAmount(tiers))}</span>
										</div>
									</div>
								) : null}
								{isNotLast ? (
									<Button className="x" onClick={removeApprovalTier.bind(null, i)}>
										<MinusCircleOutlined className="dynamic-delete-button" />
									</Button>
								) : (
									false
								)}
							</div>
						);
					})}
					<div className="ApprovalTierBottom">
						<Button
							style={{ marginTop: '0px' }}
							onClick={addApprovalTier}
							className="SearchButton"
							size={'large'}
						>
							Add Tier
						</Button>
					</div>
				</div>
			</div>
		</Modal>
	);
}

const mapStateToProps = (state, ownProps) => ({
	currentUser: state.session.currentUser,
});

const mapDispatchToProps = (dispatch) => ({});

export default withRouter<any, any>(
	connect(mapStateToProps, mapDispatchToProps)(ApprovalTiersComponent)
);
